import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { Guid } from "guid-typescript";
import { toast } from "react-toastify";
import { AxiosError } from "axios";

// Reducer
import { getReason, useReasonReducer } from "@/main/store/reason";
import { useCountryReducer } from "@/main/store/country";

// API
import { procurementApi } from "@/main/api";

// Service
import { getDefaultCountryOptionDrop, getTranslateByKey, removeEmptyValues } from "@/main/services/utils";
import { convertStringForBoolean } from "@/main/services/utilsString";
import { currencyToNumber, formatCurrency } from "@/main/services/utilsNumber";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";

// Types
import { EditProcurementItemPriceRequest, ReasonFilterRequest } from "@/main/models/request";
import { GetDetailsProcurementItemPriceResponse } from "@/main/models/response";
import { BaseResponse, KeyValueDefault } from "@/main/models/common";

// Componentes
import Box from "@/main/components/shared/box";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import Button from "@/main/components/shared/button/button";
import KogInputAmountControlled from "@/main/components/shared/inputText/inputAmountControlled";

// Styles
import { InitialStateEditPart } from "../infoOrder/infoOrder";
import * as S from "./styles";
import KogModalFadeComponent from "@/main/components/modal/ModalFadeComponent";

interface IModalEditPart {
	editPart: InitialStateEditPart;
	closeModal: () => void;
	loadViewOrder: (guidQuoteProcurement: string | undefined) => Promise<void>;
	INITIAL_STATE_EDIT_PART: InitialStateEditPart;
	refreshGrid?: () => void;
}
function ModalEditPart({
	editPart,
	closeModal,
	loadViewOrder,
	INITIAL_STATE_EDIT_PART,
	refreshGrid = () => {},
}: IModalEditPart) {
	const dispatch = useDispatch();
	const { reason: reasonReducer } = useReasonReducer();
	const countryReducer = useCountryReducer();

	const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countryReducer.countriesUser.data);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [editProcurementItem, setEditProcurementItem] = useState<GetDetailsProcurementItemPriceResponse | null>(null);
	const [openModalWarning, setOpenModalWarning] = useState<boolean>(false);
	const [messageWarning, setMessageWarning] = useState<string>("");

	const isDisabledObservation = () => convertStringForBoolean(methodSave.watch("reason")?.default);

	const INITIAL_FORM_STATE_SAVE = {
		newPrice: null as string | null,
		reason: null as KeyValueDefault | null,
		observation: "" as string,
	};
	type IReasonForm = typeof INITIAL_FORM_STATE_SAVE;
	const methodSave = useForm<IReasonForm>({
		mode: "onChange",
		defaultValues: INITIAL_FORM_STATE_SAVE,
	});

	function onSubmitSave(dataForm: IReasonForm) {
		if (dataForm.newPrice) {
			setIsLoading(true);
			const request: EditProcurementItemPriceRequest = removeEmptyValues({
				guidProcurementSupplierItem: editPart.guidProcurementSupplierItem || Guid.EMPTY,
				guidReason: String(dataForm.reason?.key),
				newPrice: currencyToNumber(dataForm.newPrice),
				reasonDescription: dataForm.observation,
			});

			procurementApi
				.editProcurementItemPrice(request)
				.then((result) => {
					if (result.data.isWarning && result.data.message.length > 0) {
						setOpenModalWarning(true);
						setMessageWarning(result.data.message || "");
						return;
					}
					toast.success(getTranslateByKey("information.savedSuccess"));
					if (editPart.guidQuoteProcurement) loadViewOrder(editPart.guidQuoteProcurement);

					refreshGrid();
					methodSave.reset();
					closeModal();
				})
				.catch((error: AxiosError<BaseResponse>) => {
					const errorData = error?.response?.data;
					if (errorData?.isWarning && errorData?.message) {
						setOpenModalWarning(true);
						setMessageWarning(errorData.message);
					}
				})
				.finally(() => {
					setIsLoading(false);
				});
		}
	}

	useEffect(() => {
		if (editPart.guidProcurementSupplierItem) {
			procurementApi
				.getDetailsProcurementItemPrice(editPart.guidProcurementSupplierItem)
				.then((res) => {
					const resData = res.data.result;
					setEditProcurementItem(resData);
					methodSave.setValue("newPrice", formatCurrency(resData.currentPrice, true));
				})
				.catch(() => {
					methodSave.reset();
					closeModal();
				});
		}
	}, [editPart.guidProcurementSupplierItem]);

	useEffect(() => {
		if (optionDefaultCountryDrop !== null) {
			const requestReason: ReasonFilterRequest = {
				guidCountry: String(optionDefaultCountryDrop?.key),
				idReasonType: ReasonTypeEnum.EditPrice,
			};
			dispatch(getReason(requestReason));
		}
	}, []);

	useEffect(() => {
		if (editPart === INITIAL_STATE_EDIT_PART) {
			methodSave.reset();
		}
	}, [editPart]);

	if (!editProcurementItem) return <LoadingComponent />;
	return (
		<>
			<Box>
				<S.BoxHeaderEditPart>
					<S.Container>
						{editProcurementItem.supplierName && (
							<S.ContainerRow>
								<S.Label>{editProcurementItem.supplierName}</S.Label>
							</S.ContainerRow>
						)}
						{(editProcurementItem.piesPartName || editProcurementItem.partType) && (
							<S.ContainerRow>
								{editProcurementItem.piesPartName && <S.Label>{editProcurementItem.piesPartName}</S.Label>}
								{editProcurementItem.partType && (
									<S.Text>
										{` - ${editProcurementItem.partType}`}
										{editProcurementItem.partBrand && ` (${editProcurementItem.partBrand})`}
									</S.Text>
								)}
							</S.ContainerRow>
						)}
						{editProcurementItem.inventoryPartName && (
							<S.ContainerRow>
								<S.Text>{editProcurementItem.inventoryPartName}</S.Text>
							</S.ContainerRow>
						)}
						{editProcurementItem.currentPrice && (
							<S.ContainerRow>
								<S.Text>{`${getTranslateByKey("information.currentPrice")}: `}</S.Text>
								<S.Label>{formatCurrency(editProcurementItem.currentPrice, true)}</S.Label>
							</S.ContainerRow>
						)}
					</S.Container>
				</S.BoxHeaderEditPart>

				<form onSubmit={methodSave.handleSubmit(onSubmitSave)} noValidate>
					<KogInputAmountControlled
						required={true}
						typeInput={"filled"}
						id="id-modalEditPart-txt-newPrice"
						name="newPrice"
						label={getTranslateByKey("information.price")}
						control={methodSave.control}
						controllerProps={{
							rules: { maxLength: 10 },
						}}
					/>
					<KogAutoCompleteControlled
						className="ignore-click"
						id="id-modalEditPart-text-reason"
						required
						label={getTranslateByKey("information.reason")}
						nameCustom="reason"
						control={methodSave.control}
						setValueCustom={methodSave.setValue}
						options={reasonReducer.data}
						isLoading={reasonReducer.isLoading}
						rulesCustom={{ required: true }}
						onChangeCustom={() => {}}
						style={{ marginTop: 10, marginBottom: 10 }}
					/>
					{isDisabledObservation() && (
						<KogInputTextControlledNew
							id="id-modalEditPart-text-observation"
							name="observation"
							required
							control={methodSave.control}
							autoFocus
							label={getTranslateByKey("information.observation")}
						/>
					)}

					<S.ButtonGroup>
						<SecondaryButton
							onClick={() => {
								methodSave.reset();
								closeModal();
							}}
							id="id-modalEditPart-button-back"
							disabled={isLoading}
						>
							{getTranslateByKey("information.back")}
						</SecondaryButton>
						<Button type="submit" id="id-modalEditPart-button-save" isLoading={isLoading}>
							{getTranslateByKey("information.save")}
						</Button>
					</S.ButtonGroup>
				</form>
			</Box>

			<KogModalFadeComponent isInModal setOpen={setOpenModalWarning} open={openModalWarning}>
				<S.ContainerModalWarning>
					<h6>{messageWarning}</h6>
					<div
						style={{
							display: "flex",
							justifyContent: "center",
						}}
					>
						<SecondaryButton type="button" onClick={() => setOpenModalWarning(false)} id="id-price-warning-button-ok">
							{getTranslateByKey("information.okLabel")}
						</SecondaryButton>
					</div>
				</S.ContainerModalWarning>
			</KogModalFadeComponent>
		</>
	);
}

export default ModalEditPart;
